<template>
  <div>
    <b-row class="mb-1">
      <b-col cols="12" md="6" lg="4" class="mb-2">
        <div class="d-flex align-items-center">
          <date-picker
            placeholder="Từ ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.fromDate"
            :disabled="onLoading"
          ></date-picker>
          <span class="mx-1"></span>
          <date-picker
            placeholder="Đến ngày"
            class="form-control form-control-sm"
            :config="dpConfigs.date"
            v-model="filter.toDate"
            :disabled="onLoading"
          ></date-picker>
        </div>
      </b-col>
      <slot name="filters-before-action" :loading="onLoading" :filter="onFilter" />
      <b-col cols="12" sm="6" md="3" lg="2" class="mb-2">
        <b-form-select
          size="sm"
          v-model="filter.action"
          :options="actionOptions"
          value-field="id"
          text-field="name"
          :disabled="onLoading"
        />
      </b-col>
      <slot name="filters-after-action" :loading="onLoading" :filter="onFilter" />
    </b-row>
    <b-row class="mb-3">
      <b-col cols="12">
        <b-button
          style="font-weight: 600; width: 70px"
          variant="primary"
          size="sm"
          :disabled="onLoading"
          @click="onFilter"
          >Lọc</b-button
        >
      </b-col>
    </b-row>

    <b-table
      :items="items"
      :fields="fields"
      :busy="onLoading"
      bordered
      hover
      small
      responsive
      show-empty
    >
      <template #table-busy>
        <div class="text-center py-3"><b-spinner small variant="primary" /></div>
      </template>
      <template #empty>
        <p class="text-center text-muted mb-0">{{ emptyText }}</p>
      </template>
      <template #cell(index)="{ index }">
        {{ (page - 1) * limit + index + 1 }}
      </template>
      <template #cell(createdAt)="{ value }">
        {{ formatTime(value) }}
      </template>
      <template #cell(actionName)="{ item }">
        {{ actionLabel(item) }}
      </template>
      <template #cell(detail)="{ item }">
        <b-button size="sm" variant="outline-primary" @click="$emit('show-detail', item)"
          >Xem</b-button
        >
      </template>
    </b-table>

    <b-row v-if="totalRow">
      <b-col lg="3" md="3" sm="12">
        <p class="mt-3 text-dark" style="font-weight: 500">
          Tổng số: {{ totalRow }}
        </p>
      </b-col>
      <b-col lg="9" md="9" sm="12">
        <BPaginationNavCustom
          :disabled="onLoading"
          :number-of-pages="totalPage"
          :current-page="page"
          :total-page="totalPage"
          no-router
          @page-change="onChangePage"
        />
      </b-col>
    </b-row>

    <slot />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import timeUtils, { formatSpecificDate } from '@/utils/date';
import { formatDate, makeToastFaile } from '@/utils/common';
import { HISTORY_TIME_FORMAT } from '@/utils/constants';

// Bảng lịch sử chỉnh sửa dùng chung: bộ lọc ngày + hành động, phân trang, gọi API.
// Wrapper truyền endpoint / tham số id / thông báo, chèn bộ lọc riêng qua slot,
// và tự mở hộp chi tiết khi nhận sự kiện show-detail.
export default {
  name: 'HistoryChangeTable',
  components: { BPaginationNavCustom },
  props: {
    endpoint: { type: String, required: true },
    // Tham số cố định của mọi lần gọi (vd { couponProgramId: 5 })
    baseParams: { type: Object, default: () => ({}) },
    // Tham số bộ lọc riêng của wrapper, đọc lúc bấm "Lọc"
    extraParams: { type: Object, default: () => ({}) },
    actionOptions: { type: Array, required: true },
    noChangeText: { type: String, required: true },
    noMatchText: { type: String, required: true },
    invalidDateRangeText: { type: String, required: true },
    // (error) => void: toast lỗi tải
    onError: { type: Function, required: true },
    actionLabel: {
      type: Function,
      default: (item) => `${item.actionName} chương trình`,
    },
    limit: { type: Number, default: 20 },
  },
  data() {
    return {
      dpConfigs: timeUtils.DP_CONFIG,
      filter: { action: null, fromDate: '', toDate: '' },
      // Bộ lọc đã bấm "Lọc" — chuyển trang giữ đúng bộ lọc này
      appliedParams: {},
      items: [],
      page: 1,
      totalRow: 0,
      totalPage: 0,
      onLoading: false,
      isFiltered: false,
      fields: [
        { key: 'index', label: 'STT', thStyle: { width: '60px' } },
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '150px' } },
        { key: 'actionName', label: 'Hành động' },
        { key: 'updatedByName', label: 'Người thực hiện' },
        { key: 'detail', label: 'Thao tác', thStyle: { width: '90px' } },
      ],
    };
  },
  computed: {
    emptyText() {
      return this.isFiltered ? this.noMatchText : this.noChangeText;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatTime(value) {
      return formatDate(value, HISTORY_TIME_FORMAT) || '';
    },
    toApiDate(value) {
      return value ? formatSpecificDate(value, 'DD/MM/YYYY', 'YYYY-MM-DD') : null;
    },
    onFilter() {
      if (this.onLoading) return;
      const fromDate = this.toApiDate(this.filter.fromDate);
      const toDate = this.toApiDate(this.filter.toDate);
      if (fromDate && toDate && fromDate > toDate) {
        makeToastFaile(this.invalidDateRangeText);
        return;
      }
      const params = {
        ...this.extraParams,
        action: this.filter.action,
        fromDate,
        toDate,
      };
      this.appliedParams = params;
      this.isFiltered = Object.values(params).some(Boolean);
      this.page = 1;
      this.fetchData();
    },
    onChangePage(page) {
      this.page = page;
      this.fetchData();
    },
    async fetchData() {
      if (this.onLoading) return;
      this.onLoading = true;
      try {
        const { data } = await ApiService.query(this.endpoint, {
          params: {
            ...this.appliedParams,
            ...this.baseParams,
            page: this.page,
            limit: this.limit,
          },
        });
        this.items = data.data.dataset;
        this.totalRow = data.data.totalRow;
        this.totalPage = data.data.totalPage;
      } catch (error) {
        this.onError(error);
      } finally {
        this.onLoading = false;
      }
    },
  },
};
</script>
